import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import { SignInContext } from '@modules/users';
import type { TChangeMockConfig } from '../../change/types';
import { simplifyAssessmentTemplate } from '../utils/simplifyAssessmentTemplate';

import {
  AUTOSAVE_STATUS,
  CHANGE_V2_CURRENT_DATE,
  TAutosaveStatus,
  TPrototypeState,
  PROTOTYPE_STATE,
} from '../constants';
import {
  MOCK_ASSESSMENT_TEMPLATES,
  MOCK_CHANGES,
  MOCK_PREVIOUS_CHANGES,
  MOCK_TEMPLATES,
  SUPPLIER_COMPONENT_CHANGE_TEMPLATE,
  TChangeV2AssessmentConfig,
  TChangeV2ConfigEntry,
  applyAssessmentConfig,
  buildDemoExecutionPlan,
  getAssessmentTemplateById,
  getTemplateById,
  mapConfigChanges,
} from '../data';
import {
  AFFECTED_OBJECT_DECISION,
  APPROVAL_DECISION,
  ASSESSMENT_VALUE_SOURCE,
  AUDIT_ACTION,
  CHANGE_STATUS,
  CONFIRMATION_STATE,
  CREATION_METHOD,
  LIFECYCLE_STEP,
  ROLE,
  SECTION_STATUS,
  TASK_PRIORITY,
  TASK_STATUS,
  TAffectedObjectDecision,
  TApprovalDecision,
  TAssessorFunction,
  TAssessmentFieldValue,
  TAssessmentSubmission,
  TAssessmentTemplateConfig,
  TAuditAction,
  TChangeAssessment,
  TChangePlan,
  TPlanTask,
  TReportedImpact,
  TTaskType,
  TChangeCategory,
  TChangeTemplate,
  TChangeV2,
  TContainment,
  TCreationMethod,
  TCriticality,
  TFieldValue,
  TPrefillKind,
  TPreviousChange,
  TRole,
  TScopeType,
  TTemplateCustomFieldConfig,
  TTemplateFieldType,
  TConditionExpression,
  TInstantiatedTemplateSnapshot,
  TTemplateInputValue,
  TTemplateRequestPrefills,
} from '../models';
import {
  activeTasks,
  buildPlanFromAssessment,
  buildSubmissionPreview,
  buildWorkflowRoute,
  createAssessmentTasks,
  createBlankChange,
  generateDisplayId,
  generateRequestId,
  generateDraftPlanningItems,
  getReleaseReadiness,
  instantiateAssessment,
  makeAuditEvent,
  resolveRequestPrefills,
} from '../utils';
import { getSourceRecordById } from '../data/mockEntities';

export type TCreateDraftInput = {
  creationMethod: TCreationMethod;
  initialTitle?: string;
  initialDescription?: string;
  initialReasonOrTrigger?: string;
  initialDesiredOutcome?: string;
  initialScopeType?: TScopeType;
  initialProducts?: string[];
  initialQmsAreas?: string[];
  templateId?: string;
  // The template-specific facts entered in the setup form. Resolved into the
  // request text and stored as an immutable snapshot on the created Change.
  templateInputValues?: Record<string, TTemplateInputValue>;
  previousChangeId?: string;
  sourceRecordId?: string;
};

export type TAcceptPayload = {
  changeManager: string;
  targetDate?: string;
  changeCategory?: TChangeCategory;
  scopeType?: TScopeType;
  criticality?: TCriticality;
  assessorFunctions: TAssessorFunction[];
  templateId?: string;
  changeSubtype?: string;
  containment: TContainment;
  decisionRationale?: string;
};

// Lightweight `Add custom field` input (prompt Part 3). Only the essentials.
export type TAddCustomFieldInput = {
  label: string;
  fieldType: TTemplateFieldType;
  sectionId: string;
  required: boolean;
  prefillKind: TPrefillKind;
  prefilledValue?: TFieldValue;
  defaultValue?: TFieldValue;
  visibleWhen?: TConditionExpression;
};

export type TApprovalSignature = {
  role: string;
  meaning: string;
  comment?: string;
};

export type TApprovalInput = {
  decision: TApprovalDecision;
  signature: TApprovalSignature;
  conditions?: string[];
  returnRationale?: string;
  returnSections?: string[];
  rejectRationale?: string;
};

// -- Plan and execution inputs (Screens 7 and 8) -----------------------------

export type TAddTaskInput = {
  title: string;
  taskType: TTaskType;
  workPackageId: string;
  owner?: string;
  responsibleFunction?: string;
  dueDate?: string;
  description?: string;
  required: boolean;
};

export type TCompleteTaskInput = {
  deviations?: string;
};

export type TReportImpactInput = {
  description: string;
  affectedObject: string;
  materialScopeChange: 'Yes' | 'No' | 'Unsure';
  suggestedFollowUp?: string;
};

export type TChangeV2StoreValue = {
  changes: TChangeV2[];
  templates: TChangeTemplate[];
  assessmentTemplates: TAssessmentTemplateConfig[];
  selectedTemplateId: string;
  previousChanges: TPreviousChange[];
  currentUser: string;

  role: TRole;
  setRole: (role: TRole) => void;

  signedBaseline: boolean;
  setSignedBaseline: (value: boolean) => void;
  saveFailureArmed: boolean;
  permissionDenied: boolean;
  autosaveStatus: TAutosaveStatus;
  setAutosaveStatus: (status: TAutosaveStatus) => void;

  getChange: (id: string) => TChangeV2 | undefined;
  getAssessmentTemplate: (id?: string) => TAssessmentTemplateConfig | undefined;

  createDraft: (input: TCreateDraftInput) => TChangeV2;
  patchRequest: (id: string, patch: Partial<TChangeV2>) => void;
  recordAutosave: (id: string) => void;
  submitRequest: (id: string) => void;
  cancelDraft: (id: string) => void;
  applyTemplate: (id: string, templateId: string) => void;

  triageAccept: (id: string, payload: TAcceptPayload) => void;
  triageReturn: (id: string, rationale: string) => void;
  triageReject: (id: string, rationale: string) => void;
  triageDuplicate: (id: string, duplicateOfId: string, rationale: string) => void;

  // Assessment (Screen 5)
  ensureAssessment: (id: string) => void;
  setAssessmentAnswer: (id: string, questionId: string, value: TFieldValue) => void;
  setAnswerComment: (id: string, questionId: string, comment: string) => void;
  setAnswerRationale: (id: string, questionId: string, rationale: string) => void;
  setCustomFieldValue: (id: string, fieldId: string, value: TFieldValue) => void;
  confirmAnswerSuggestion: (id: string, questionId: string) => void;
  completeSection: (id: string, sectionId: string) => void;
  reopenSection: (id: string, sectionId: string) => void;
  setAffectedObjectDecision: (
    id: string,
    objectId: string,
    decision: TAffectedObjectDecision,
    rationale?: string
  ) => void;
  submitAssessment: (id: string) => TAssessmentSubmission | undefined;
  addTemplateCustomField: (input: TAddCustomFieldInput) => void;

  // Approval within the assessment step
  decideApproval: (id: string, input: TApprovalInput) => void;

  // Implementation plan (Screen 7)
  ensurePlan: (id: string) => void;
  updatePlanTask: (id: string, taskId: string, patch: Partial<TPlanTask>) => void;
  addPlanTask: (id: string, input: TAddTaskInput) => void;
  removePlanTask: (id: string, taskId: string) => void;
  startExecution: (id: string) => void;

  // Execution cockpit (Screen 8)
  startTask: (id: string, taskId: string) => void;
  completeTask: (id: string, taskId: string, input: TCompleteTaskInput) => void;
  reportNewImpact: (id: string, input: TReportImpactInput) => void;
  beginCompletion: (id: string) => void;
  checkReleaseReadiness: (id: string) => void;
  approveRelease: (id: string) => void;
  completeEffectivenessReview: (id: string) => void;
  closeChange: (id: string) => void;

  addComment: (id: string, body: string) => void;

  applyPrototypeState: (id: string, state: TPrototypeState) => void;
  retrySave: (id: string) => void;
};

const noop = () => undefined;

export const ChangeV2Context = createContext<TChangeV2StoreValue>({
  changes: [],
  templates: [],
  assessmentTemplates: [],
  selectedTemplateId: SUPPLIER_COMPONENT_CHANGE_TEMPLATE.id,
  previousChanges: [],
  currentUser: 'You',
  role: ROLE.CHANGE_MANAGER,
  setRole: noop,
  signedBaseline: false,
  setSignedBaseline: noop,
  saveFailureArmed: false,
  permissionDenied: false,
  autosaveStatus: AUTOSAVE_STATUS.SAVED,
  setAutosaveStatus: noop,
  getChange: () => undefined,
  getAssessmentTemplate: () => undefined,
  createDraft: () => createBlankChange('You'),
  patchRequest: noop,
  recordAutosave: noop,
  submitRequest: noop,
  cancelDraft: noop,
  applyTemplate: noop,
  triageAccept: noop,
  triageReturn: noop,
  triageReject: noop,
  triageDuplicate: noop,
  ensureAssessment: noop,
  setAssessmentAnswer: noop,
  setAnswerComment: noop,
  setAnswerRationale: noop,
  setCustomFieldValue: noop,
  confirmAnswerSuggestion: noop,
  completeSection: noop,
  reopenSection: noop,
  setAffectedObjectDecision: noop,
  submitAssessment: () => undefined,
  addTemplateCustomField: noop,
  decideApproval: noop,
  ensurePlan: noop,
  updatePlanTask: noop,
  addPlanTask: noop,
  removePlanTask: noop,
  startExecution: noop,
  startTask: noop,
  completeTask: noop,
  reportNewImpact: noop,
  beginCompletion: noop,
  checkReleaseReadiness: noop,
  approveRelease: noop,
  completeEffectivenessReview: noop,
  closeChange: noop,
  addComment: noop,
  applyPrototypeState: noop,
  retrySave: noop,
});

// -- Pure helpers ------------------------------------------------------------

const applyPrefills = (change: TChangeV2, prefills: TTemplateRequestPrefills): TChangeV2 => ({
  ...change,
  title: change.title || prefills.title || '',
  description: change.description || prefills.description || '',
  reasonOrTrigger: change.reasonOrTrigger || prefills.reasonOrTrigger || '',
  desiredOutcome: change.desiredOutcome || prefills.desiredOutcome,
  implementationProposal: change.implementationProposal || prefills.implementationProposal,
  scopeType: change.scopeType || prefills.scopeType,
  urgency: prefills.urgency ?? change.urgency,
  changeCategory: change.changeCategory || prefills.changeCategory,
  affectedQmsAreas: change.affectedQmsAreas.length > 0 ? change.affectedQmsAreas : (prefills.affectedQmsAreas ?? []),
});

const displayValue = (value: TFieldValue): string => {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Not set';
    return value.map((entry) => (typeof entry === 'object' ? entry.label : String(entry))).join(', ');
  }
  if (typeof value === 'object') return value.label;
  return String(value);
};

const normalizeValue = (value: TFieldValue): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return JSON.stringify(value.map((entry) => (typeof entry === 'object' ? entry.id : entry)));
  if (typeof value === 'object') return value.id;
  return String(value);
};

const valuesEqual = (a: TFieldValue, b: TFieldValue): boolean => normalizeValue(a) === normalizeValue(b);

type TValueEdit = { next: TAssessmentFieldValue; action: TAuditAction; description: string; changed: boolean };

// Derives the confirmation-state transition and audit action for one value edit.
// A suggestion set to the same value is a confirmation; a different value is a
// change; an edited request fact or default is a prefill edit.
const deriveValueEdit = (
  existing: TAssessmentFieldValue,
  newValue: TFieldValue,
  actor: string,
  at: string,
  label: string
): TValueEdit => {
  const changed = !valuesEqual(existing.value, newValue);
  if (!changed) {
    if (existing.confirmationState === CONFIRMATION_STATE.UNCONFIRMED) {
      return {
        next: {
          ...existing,
          confirmationState: CONFIRMATION_STATE.CONFIRMED_BY_ASSESSOR,
          lastUpdatedBy: actor,
          lastUpdatedAt: at,
        },
        action: AUDIT_ACTION.SUGGESTED_ANSWER_CONFIRMED,
        description: `Confirmed suggested answer: ${label} = ${displayValue(newValue)}.`,
        changed: false,
      };
    }
    return { next: existing, action: AUDIT_ACTION.FIELD_CHANGED, description: '', changed: false };
  }

  const previous = displayValue(existing.value);
  let action: TAuditAction = AUDIT_ACTION.FIELD_CHANGED;
  if (existing.confirmationState === CONFIRMATION_STATE.UNCONFIRMED) action = AUDIT_ACTION.SUGGESTED_ANSWER_CHANGED;
  else if (
    existing.source === ASSESSMENT_VALUE_SOURCE.REQUEST ||
    existing.source === ASSESSMENT_VALUE_SOURCE.TEMPLATE_DEFAULT
  )
    action = AUDIT_ACTION.PREFILLED_ANSWER_EDITED;

  return {
    next: {
      ...existing,
      value: newValue,
      source: ASSESSMENT_VALUE_SOURCE.ASSESSOR,
      confirmationState: CONFIRMATION_STATE.CHANGED_BY_ASSESSOR,
      lastUpdatedBy: actor,
      lastUpdatedAt: at,
    },
    action,
    description: `${label}: "${previous}" → "${displayValue(newValue)}".`,
    changed: true,
  };
};

// Finds which section a question or custom field belongs to in the snapshot.
const sectionIdOfQuestion = (assessment: TChangeAssessment, questionId: string): string | undefined =>
  assessment.snapshot.assessmentSections.find((section) => section.questions.some((q) => q.id === questionId))?.id;

const questionLabel = (assessment: TChangeAssessment, questionId: string): string => {
  for (const section of assessment.snapshot.assessmentSections) {
    const question = section.questions.find((q) => q.id === questionId);
    if (question) return question.label;
  }
  return questionId;
};

const customFieldConfig = (assessment: TChangeAssessment, fieldId: string): TTemplateCustomFieldConfig | undefined =>
  assessment.snapshot.customFields.find((field) => field.id === fieldId);

// Seeds one Change's assessment from the selected template plus config overrides.
const seedAssessments = (
  changes: TChangeV2[],
  configs: TChangeV2AssessmentConfig[] | undefined,
  templates: TAssessmentTemplateConfig[],
  selectedTemplateId: string,
  instantiatedAt: string
): TChangeV2[] => {
  if (!configs || configs.length === 0) return changes;
  const template = getAssessmentTemplateById(templates, selectedTemplateId);
  if (!template) return changes;

  return changes.map((change) => {
    const config = configs.find((entry) => entry.changeDisplayId === change.displayId);
    if (!config) return change;
    // Sample values belong to this instantiated assessment, not to the reusable
    // template or to the next user's creation form.
    const base = instantiateAssessment(template, instantiatedAt, config.templateInputValues ?? {});
    const assessment = applyAssessmentConfig(base, config, 'System', instantiatedAt);
    return {
      ...change,
      template: change.template ?? template.id,
      templateVersion: template.version,
      changeSubtype: change.changeSubtype ?? template.name,
      assessment,
    };
  });
};

export const ChangeV2Provider = ({ children }: { children: ReactNode }) => {
  const { organization, user } = useContext(SignInContext);
  const changeV2Config = organization.mock?.changeV2;
  const configTemplates = changeV2Config?.templates as TChangeTemplate[] | undefined;
  const configChanges = changeV2Config?.changes as TChangeV2ConfigEntry[] | undefined;
  const configAssessmentTemplates = changeV2Config?.assessmentTemplates as TAssessmentTemplateConfig[] | undefined;
  const configAssessment = changeV2Config?.assessment as
    | TChangeV2AssessmentConfig
    | TChangeV2AssessmentConfig[]
    | undefined;
  const configSelectedTemplateId = changeV2Config?.selectedTemplateId as string | undefined;
  const implementationPlan = (organization.mock?.change as TChangeMockConfig | undefined)?.newData?.implementationPlan;
  const initialAssessmentTemplates = (
    configAssessmentTemplates && configAssessmentTemplates.length > 0
      ? configAssessmentTemplates
      : MOCK_ASSESSMENT_TEMPLATES
  ).map((template) => simplifyAssessmentTemplate(template, implementationPlan));
  const templates = useMemo<TChangeTemplate[]>(
    () =>
      (configTemplates && configTemplates.length > 0 ? configTemplates : MOCK_TEMPLATES).map((template) => ({
        ...template,
        creationInputs: template.creationInputs?.map((input) => ({ ...input, required: false })),
      })),
    [configTemplates]
  );

  // Assessment templates are mutable so the lightweight `Add custom field`
  // prototype can extend the org template configuration in memory.
  const [assessmentTemplates, setAssessmentTemplates] =
    useState<TAssessmentTemplateConfig[]>(initialAssessmentTemplates);
  const selectedTemplateId = configSelectedTemplateId ?? SUPPLIER_COMPONENT_CHANGE_TEMPLATE.id;

  const [changes, setChanges] = useState<TChangeV2[]>(() => {
    const base = mapConfigChanges(configChanges) ?? MOCK_CHANGES;
    const initialTemplates = initialAssessmentTemplates;
    const assessmentConfigs = configAssessment
      ? Array.isArray(configAssessment)
        ? configAssessment
        : [configAssessment]
      : undefined;
    const seeded = seedAssessments(
      base,
      assessmentConfigs,
      initialTemplates,
      selectedTemplateId,
      CHANGE_V2_CURRENT_DATE.toISOString()
    );
    // A config-authored change already in execution gets the fully populated demo
    // plan so Screen 8 is immediately viewable in the register.
    return seeded.map((change) =>
      change.status === CHANGE_STATUS.IN_EXECUTION && !change.plan
        ? { ...change, plan: buildDemoExecutionPlan() }
        : change
    );
  });

  const [role, setRole] = useState<TRole>(ROLE.SYSTEM_ADMINISTRATOR);
  const [signedBaseline, setSignedBaseline] = useState(false);
  const [saveFailureArmed, setSaveFailureArmed] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<TAutosaveStatus>(AUTOSAVE_STATUS.SAVED);

  const currentUser = user.name || user.mail;

  const updateChange = (id: string, updater: (change: TChangeV2) => TChangeV2) =>
    setChanges((prev) => prev.map((change) => (change.id === id ? updater(change) : change)));

  // Updates a Change that has an assessment; a Change with no assessment is left
  // untouched so callers never need to null-check the record.
  const updateAssessment = (id: string, updater: (assessment: TChangeAssessment, change: TChangeV2) => TChangeV2) =>
    updateChange(id, (change) => (change.assessment ? updater(change.assessment, change) : change));

  const resolveAssessmentTemplateForAccept = (appliedTemplateId?: string): TAssessmentTemplateConfig | undefined => {
    const direct = getAssessmentTemplateById(assessmentTemplates, appliedTemplateId);
    if (direct) return direct;
    const stripped = appliedTemplateId?.replace(/^t-/, '');
    const byStripped = getAssessmentTemplateById(assessmentTemplates, stripped);
    if (byStripped) return byStripped;
    // Fall back to the selected template so the demo assessment always renders.
    return getAssessmentTemplateById(assessmentTemplates, selectedTemplateId);
  };

  const value = useMemo<TChangeV2StoreValue>(() => {
    const getChange = (id: string) => changes.find((change) => change.id === id);
    const getAssessmentTemplate = (id?: string) =>
      getAssessmentTemplateById(assessmentTemplates, id ?? selectedTemplateId);

    const createDraft = (input: TCreateDraftInput): TChangeV2 => {
      let draft = createBlankChange(currentUser, {
        requestId: generateRequestId(changes),
        title: input.initialTitle ?? '',
        description: input.initialDescription ?? '',
        reasonOrTrigger: input.initialReasonOrTrigger ?? '',
        desiredOutcome: input.initialDesiredOutcome,
        scopeType: input.initialScopeType,
        affectedProducts: input.initialProducts ?? [],
        affectedQmsAreas: input.initialQmsAreas ?? [],
      });

      draft.auditEvents = [makeAuditEvent(currentUser, AUDIT_ACTION.DRAFT_CREATED, 'Draft created.')];

      if (input.creationMethod === CREATION_METHOD.TEMPLATE && input.templateId) {
        const template = getTemplateById(templates, input.templateId);
        if (template) {
          // Resolve the template's `{{input:...}}` placeholders against the entered
          // facts, then apply the resolved text as ordinary request prefills.
          const inputValues = input.templateInputValues ?? {};
          const resolved = resolveRequestPrefills(template, inputValues, 'create');
          const resolvedPrefills: TTemplateRequestPrefills = {
            title: resolved.title || undefined,
            description: resolved.description || undefined,
            reasonOrTrigger: resolved.reasonOrTrigger || undefined,
            desiredOutcome: resolved.desiredOutcome || undefined,
            implementationProposal: resolved.implementationProposal || undefined,
            scopeType: resolved.scopeType,
            urgency: resolved.urgency,
            changeCategory: resolved.changeCategory,
            affectedQmsAreas: resolved.affectedQmsAreas,
          };
          draft = applyPrefills(draft, resolvedPrefills);
          draft.template = template.id;
          draft.templateVersion = template.version;
          draft.assessorFunctions = template.suggestedRoles ?? [];

          // Any products picked as template inputs seed the affected-products scope.
          const productInputLabels = (template.creationInputs ?? [])
            .filter((field) => field.fieldType === 'productReference' || field.fieldType === 'productMultiReference')
            .map((field) => inputValues[field.id])
            .flatMap((value) => {
              if (value && typeof value === 'object' && !Array.isArray(value)) return [value.label];
              if (Array.isArray(value))
                return value.map((entry) => (typeof entry === 'object' ? entry.label : entry)).filter(Boolean);
              return [];
            })
            .filter((label): label is string => Boolean(label));
          if (draft.affectedProducts.length === 0 && productInputLabels.length > 0) {
            draft.affectedProducts = productInputLabels;
          }

          const snapshot: TInstantiatedTemplateSnapshot = {
            templateId: template.id,
            templateVersion: template.version ?? 1,
            inputValues,
            instantiatedAt: new Date().toISOString(),
          };
          draft.templateSnapshot = snapshot;

          draft.auditEvents.push(
            makeAuditEvent(currentUser, AUDIT_ACTION.TEMPLATE_APPLIED, `Template "${template.name}" applied.`),
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.TEMPLATE_INSTANTIATED,
              `Template "${template.name}" (v${snapshot.templateVersion}) instantiated with ${
                Object.keys(inputValues).length
              } input value${Object.keys(inputValues).length === 1 ? '' : 's'}.`
            )
          );
        }
      }

      if (input.creationMethod === CREATION_METHOD.PREVIOUS && input.previousChangeId) {
        const previous = MOCK_PREVIOUS_CHANGES.find((change) => change.id === input.previousChangeId);
        if (previous) {
          draft.scopeType = draft.scopeType ?? previous.scopeType;
          draft.auditEvents.push(
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.FIELD_CHANGED,
              `Structure reused from ${previous.displayId}. Approvals and evidence were not copied.`
            )
          );
        }
      }

      if (input.creationMethod === CREATION_METHOD.SOURCE && input.sourceRecordId) {
        const source = getSourceRecordById(input.sourceRecordId);
        if (source) {
          draft.sourceRecord = source.id;
          draft.title = draft.title || source.title;
          draft.reasonOrTrigger = draft.reasonOrTrigger || source.summary;
          if (source.type === 'CAPA') {
            draft.capaRequired = true;
            draft.capaId = source.reference;
          }
        }
      }

      setChanges((prev) => [draft, ...prev]);
      return draft;
    };

    const patchRequest = (id: string, patch: Partial<TChangeV2>) =>
      updateChange(id, (change) => ({ ...change, ...patch }));

    const recordAutosave = (id: string) =>
      updateChange(id, (change) => ({
        ...change,
        auditEvents: [
          ...change.auditEvents,
          makeAuditEvent(currentUser, AUDIT_ACTION.REQUEST_AUTOSAVED, 'Request autosaved.'),
        ],
      }));

    const submitRequest = (id: string) =>
      updateChange(id, (change) => {
        const displayId = change.displayId ?? generateDisplayId(changes);
        const assessmentTasks = createAssessmentTasks(change.assessorFunctions);
        const assessmentTemplate = resolveAssessmentTemplateForAccept(change.template);
        const assessment = assessmentTemplate
          ? instantiateAssessment(
              assessmentTemplate,
              new Date().toISOString(),
              change.templateSnapshot?.inputValues ?? {}
            )
          : undefined;
        const changeManager = change.changeManager ?? currentUser;
        const events = [
          makeAuditEvent(currentUser, AUDIT_ACTION.REQUEST_SUBMITTED, 'Request submitted for assessment.'),
          makeAuditEvent(currentUser, AUDIT_ACTION.CHANGE_ID_GENERATED, `Change ID ${displayId} generated.`),
          makeAuditEvent(
            currentUser,
            AUDIT_ACTION.CHANGE_MANAGER_ASSIGNED,
            `${changeManager} assigned as change manager.`
          ),
          ...change.assessorFunctions.map((assessorFunction) =>
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.ASSESSOR_FUNCTION_ADDED,
              `Assessor function added: ${assessorFunction}.`
            )
          ),
          ...assessmentTasks.map((task) =>
            makeAuditEvent(currentUser, AUDIT_ACTION.ASSESSMENT_TASK_CREATED, `Assessment task created: ${task.title}.`)
          ),
        ];
        if (assessmentTemplate)
          events.push(
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.ASSESSMENT_INSTANTIATED,
              `Assessment instantiated from template "${assessmentTemplate.name}" (v${assessmentTemplate.version}).`
            )
          );

        return {
          ...change,
          displayId,
          status: CHANGE_STATUS.IN_ASSESSMENT,
          lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
          returnInfo: undefined,
          changeManager,
          currentOwner: changeManager,
          assessment: assessment ?? change.assessment,
          workflowRoute: buildWorkflowRoute({
            assessorFunctions: change.assessorFunctions,
            containmentRequired: change.immediateContainment.required,
            hasTemplate: Boolean(change.template),
          }).map((routeStep) => routeStep.step),
          nextAction: 'Begin assessment',
          progress: Math.max(change.progress, 30),
          tasks: [...change.tasks, ...assessmentTasks],
          auditEvents: [...change.auditEvents, ...events],
        };
      });

    const cancelDraft = (id: string) =>
      updateChange(id, (change) => ({
        ...change,
        status: CHANGE_STATUS.CANCELLED,
        currentOwner: undefined,
        nextAction: 'Cancelled',
        auditEvents: [
          ...change.auditEvents,
          makeAuditEvent(currentUser, AUDIT_ACTION.FIELD_CHANGED, 'Draft cancelled by the initiator.'),
        ],
      }));

    const applyTemplate = (id: string, templateId: string) => {
      const template = getTemplateById(templates, templateId);
      if (!template) return;
      updateChange(id, (change) => {
        const resolved = resolveRequestPrefills(template, change.templateSnapshot?.inputValues ?? {}, 'create');
        return {
          ...applyPrefills(change, {
            ...resolved,
            title: resolved.title || undefined,
            description: resolved.description || undefined,
            reasonOrTrigger: resolved.reasonOrTrigger || undefined,
            desiredOutcome: resolved.desiredOutcome || undefined,
            implementationProposal: resolved.implementationProposal || undefined,
          }),
          template: template.id,
          templateVersion: template.version,
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.TEMPLATE_APPLIED, `Template "${template.name}" applied.`),
          ],
        };
      });
    };

    const triageAccept = (id: string, payload: TAcceptPayload) =>
      updateChange(id, (change) => {
        const displayId = change.displayId ?? generateDisplayId(changes);
        const assessmentTasks = createAssessmentTasks(payload.assessorFunctions);
        const template = payload.templateId ? getTemplateById(templates, payload.templateId) : undefined;
        const containmentTask = payload.containment.required
          ? {
              id: `${id}-containment`,
              title: payload.containment.description
                ? `Containment: ${payload.containment.description}`
                : 'Immediate containment',
              kind: 'containment' as const,
              owner: payload.containment.owner,
              dueDate: payload.containment.dueDate,
              status: 'Open' as const,
              urgent: true,
            }
          : undefined;

        const nowIso = new Date().toISOString();
        const assessmentTemplate = resolveAssessmentTemplateForAccept(payload.templateId ?? change.template);
        const assessment = assessmentTemplate
          ? instantiateAssessment(assessmentTemplate, nowIso, change.templateSnapshot?.inputValues ?? {})
          : undefined;

        const events = [
          makeAuditEvent(currentUser, AUDIT_ACTION.TRIAGE_ACCEPTED, 'Accepted for assessment.'),
          makeAuditEvent(currentUser, AUDIT_ACTION.CHANGE_ID_GENERATED, `Change ID ${displayId} generated.`),
          makeAuditEvent(
            currentUser,
            AUDIT_ACTION.CHANGE_MANAGER_ASSIGNED,
            `${payload.changeManager} assigned as change manager.`
          ),
          ...payload.assessorFunctions.map((assessorFunction) =>
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.ASSESSOR_FUNCTION_ADDED,
              `Assessor function added: ${assessorFunction}.`
            )
          ),
          ...assessmentTasks.map((task) =>
            makeAuditEvent(currentUser, AUDIT_ACTION.ASSESSMENT_TASK_CREATED, `Assessment task created: ${task.title}.`)
          ),
        ];
        if (containmentTask)
          events.push(
            makeAuditEvent(currentUser, AUDIT_ACTION.CONTAINMENT_TASK_CREATED, 'Immediate containment task created.')
          );
        if (assessmentTemplate)
          events.push(
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.ASSESSMENT_INSTANTIATED,
              `Assessment instantiated from template "${assessmentTemplate.name}" (v${assessmentTemplate.version}).`
            )
          );

        return {
          ...change,
          displayId,
          status: CHANGE_STATUS.IN_ASSESSMENT,
          lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
          changeManager: payload.changeManager,
          currentOwner: payload.changeManager,
          targetDate: payload.targetDate ?? change.targetDate,
          changeCategory: payload.changeCategory ?? change.changeCategory,
          scopeType: payload.scopeType ?? change.scopeType,
          criticality: payload.criticality ?? change.criticality,
          changeSubtype: payload.changeSubtype ?? assessmentTemplate?.name ?? change.changeSubtype,
          assessorFunctions: payload.assessorFunctions,
          immediateContainment: payload.containment,
          template: template?.id ?? change.template,
          templateVersion: assessmentTemplate?.version ?? change.templateVersion,
          assessment: assessment ?? change.assessment,
          workflowRoute: buildWorkflowRoute({
            assessorFunctions: payload.assessorFunctions,
            containmentRequired: payload.containment.required,
            hasTemplate: Boolean(template),
          }).map((routeStep) => routeStep.step),
          nextAction: 'Begin assessment',
          progress: Math.max(change.progress, 30),
          tasks: [...change.tasks, ...assessmentTasks, ...(containmentTask ? [containmentTask] : [])],
          auditEvents: [...change.auditEvents, ...events],
        };
      });

    const triageReturn = (id: string, rationale: string) =>
      updateChange(id, (change) => ({
        ...change,
        status: CHANGE_STATUS.DRAFT,
        lifecycleStep: LIFECYCLE_STEP.REQUEST,
        returnInfo: { rationale, returnedBy: currentUser, returnedAt: new Date().toISOString() },
        currentOwner: change.initiator,
        nextAction: 'Address the clarification and resubmit',
        auditEvents: [
          ...change.auditEvents,
          makeAuditEvent(currentUser, AUDIT_ACTION.REQUEST_RETURNED, `Returned for clarification: ${rationale}`),
        ],
      }));

    const triageReject = (id: string, rationale: string) =>
      updateChange(id, (change) => ({
        ...change,
        status: CHANGE_STATUS.CANCELLED,
        rejectInfo: { rationale, rejectedBy: currentUser, rejectedAt: new Date().toISOString() },
        currentOwner: undefined,
        nextAction: 'Rejected',
        auditEvents: [
          ...change.auditEvents,
          makeAuditEvent(currentUser, AUDIT_ACTION.REQUEST_REJECTED, `Request rejected: ${rationale}`),
        ],
      }));

    const triageDuplicate = (id: string, duplicateOfId: string, rationale: string) =>
      updateChange(id, (change) => {
        const canonical = changes.find((candidate) => candidate.id === duplicateOfId);
        return {
          ...change,
          status: CHANGE_STATUS.CANCELLED,
          duplicateInfo: {
            duplicateOfId,
            duplicateOfDisplayId: canonical?.displayId ?? 'the canonical change',
            rationale,
          },
          currentOwner: undefined,
          nextAction: 'Closed as duplicate',
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.DUPLICATE_LINKED,
              `Linked as duplicate of ${canonical?.displayId ?? duplicateOfId}: ${rationale}`
            ),
          ],
        };
      });

    // -- Assessment (Screen 5) ------------------------------------------------

    const ensureAssessment = (id: string) =>
      updateChange(id, (change) => {
        if (change.assessment) return change;
        const template =
          getAssessmentTemplateById(assessmentTemplates, change.template) ??
          getAssessmentTemplateById(assessmentTemplates, selectedTemplateId);
        if (!template) return change;
        const assessment = instantiateAssessment(
          template,
          new Date().toISOString(),
          change.templateSnapshot?.inputValues ?? {}
        );
        return {
          ...change,
          assessment,
          templateVersion: template.version,
          changeSubtype: change.changeSubtype ?? template.name,
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.ASSESSMENT_INSTANTIATED,
              `Assessment instantiated from template "${template.name}" (v${template.version}).`
            ),
          ],
        };
      });

    // Marks the section containing an edited item back to In progress if it was
    // complete, recording the reopen. Returns updated progress + events.
    const reopenIfComplete = (assessment: TChangeAssessment, sectionId: string | undefined, actor: string) => {
      if (!sectionId)
        return { sectionProgress: assessment.sectionProgress, events: [] as ReturnType<typeof makeAuditEvent>[] };
      const progress = assessment.sectionProgress[sectionId];
      if (!progress || progress.status !== SECTION_STATUS.COMPLETE) {
        return { sectionProgress: assessment.sectionProgress, events: [] };
      }
      return {
        sectionProgress: {
          ...assessment.sectionProgress,
          [sectionId]: { sectionId, status: SECTION_STATUS.IN_PROGRESS },
        },
        events: [makeAuditEvent(actor, AUDIT_ACTION.SECTION_REOPENED, `Completed section reopened after an edit.`)],
      };
    };

    const setAssessmentAnswer = (id: string, questionId: string, newValue: TFieldValue) =>
      updateAssessment(id, (assessment, change) => {
        const existing = assessment.answers[questionId];
        if (!existing) return change;
        const edit = deriveValueEdit(
          existing,
          newValue,
          currentUser,
          new Date().toISOString(),
          questionLabel(assessment, questionId)
        );
        const sectionId = sectionIdOfQuestion(assessment, questionId);
        const reopen = reopenIfComplete(assessment, sectionId, currentUser);
        const events = edit.description ? [makeAuditEvent(currentUser, edit.action, edit.description)] : [];
        const nextAnswers = { ...assessment.answers, [questionId]: edit.next };
        // Keep a bound custom field in sync (single value, no competing copies).
        const nextCustomFieldValues = { ...assessment.customFieldValues };
        const boundQuestion = assessment.snapshot.assessmentSections
          .flatMap((s) => s.questions)
          .find((q) => q.id === questionId);
        if (boundQuestion?.bindsToCustomFieldId && nextCustomFieldValues[boundQuestion.bindsToCustomFieldId]) {
          nextCustomFieldValues[boundQuestion.bindsToCustomFieldId] = {
            ...nextCustomFieldValues[boundQuestion.bindsToCustomFieldId],
            value: edit.next.value,
            lastUpdatedBy: currentUser,
            lastUpdatedAt: edit.next.lastUpdatedAt,
          };
        }
        return {
          ...change,
          assessment: {
            ...assessment,
            answers: nextAnswers,
            customFieldValues: nextCustomFieldValues,
            sectionProgress: reopen.sectionProgress,
          },
          auditEvents: [...change.auditEvents, ...events, ...reopen.events],
        };
      });

    const setAnswerComment = (id: string, questionId: string, comment: string) =>
      updateAssessment(id, (assessment, change) => {
        const existing = assessment.answers[questionId];
        if (!existing) return change;
        return {
          ...change,
          assessment: {
            ...assessment,
            answers: {
              ...assessment.answers,
              [questionId]: {
                ...existing,
                comment,
                lastUpdatedBy: currentUser,
                lastUpdatedAt: new Date().toISOString(),
              },
            },
          },
        };
      });

    const setAnswerRationale = (id: string, questionId: string, rationale: string) =>
      updateAssessment(id, (assessment, change) => {
        const existing = assessment.answers[questionId];
        if (!existing) return change;
        return {
          ...change,
          assessment: {
            ...assessment,
            answers: {
              ...assessment.answers,
              [questionId]: {
                ...existing,
                rationale,
                lastUpdatedBy: currentUser,
                lastUpdatedAt: new Date().toISOString(),
              },
            },
          },
        };
      });

    const confirmAnswerSuggestion = (id: string, questionId: string) =>
      updateAssessment(id, (assessment, change) => {
        const existing = assessment.answers[questionId];
        if (!existing) return change;
        const next: TAssessmentFieldValue = {
          ...existing,
          confirmationState: CONFIRMATION_STATE.CONFIRMED_BY_ASSESSOR,
          lastUpdatedBy: currentUser,
          lastUpdatedAt: new Date().toISOString(),
        };
        return {
          ...change,
          assessment: { ...assessment, answers: { ...assessment.answers, [questionId]: next } },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.SUGGESTED_ANSWER_CONFIRMED,
              `Confirmed suggested answer: ${questionLabel(assessment, questionId)} = ${displayValue(existing.value)}.`
            ),
          ],
        };
      });

    const setCustomFieldValue = (id: string, fieldId: string, newValue: TFieldValue) =>
      updateAssessment(id, (assessment, change) => {
        const existing = assessment.customFieldValues[fieldId];
        if (!existing) return change;
        const field = customFieldConfig(assessment, fieldId);
        const edit = deriveValueEdit(
          existing,
          newValue,
          currentUser,
          new Date().toISOString(),
          field?.label ?? fieldId
        );
        const reopen = reopenIfComplete(assessment, field?.sectionId, currentUser);
        const events = edit.description ? [makeAuditEvent(currentUser, edit.action, edit.description)] : [];
        return {
          ...change,
          assessment: {
            ...assessment,
            customFieldValues: { ...assessment.customFieldValues, [fieldId]: edit.next },
            sectionProgress: reopen.sectionProgress,
          },
          auditEvents: [...change.auditEvents, ...events, ...reopen.events],
        };
      });

    const completeSection = (id: string, sectionId: string) =>
      updateAssessment(id, (assessment, change) => {
        const section = assessment.snapshot.assessmentSections.find((s) => s.id === sectionId);
        if (!section) return change;
        const now = new Date().toISOString();
        return {
          ...change,
          assessment: {
            ...assessment,
            sectionProgress: {
              ...assessment.sectionProgress,
              [sectionId]: { sectionId, status: SECTION_STATUS.COMPLETE, completedBy: currentUser, completedAt: now },
            },
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.SECTION_COMPLETED, `Section completed: ${section.title}.`),
          ],
        };
      });

    const reopenSection = (id: string, sectionId: string) =>
      updateAssessment(id, (assessment, change) => {
        const section = assessment.snapshot.assessmentSections.find((s) => s.id === sectionId);
        if (!section) return change;
        return {
          ...change,
          assessment: {
            ...assessment,
            sectionProgress: {
              ...assessment.sectionProgress,
              [sectionId]: { sectionId, status: SECTION_STATUS.IN_PROGRESS },
            },
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.SECTION_REOPENED, `Section reopened: ${section.title}.`),
          ],
        };
      });

    const setAffectedObjectDecision = (
      id: string,
      objectId: string,
      decision: TAffectedObjectDecision,
      rationale?: string
    ) =>
      updateAssessment(id, (assessment, change) => {
        const candidate = assessment.snapshot.candidateAffectedObjects.find((object) => object.id === objectId);
        const label = candidate?.label ?? objectId;
        const affectedObjects = assessment.affectedObjects.map((object) =>
          object.objectId === objectId ? { ...object, decision, rationale } : object
        );
        const action =
          decision === AFFECTED_OBJECT_DECISION.NOT_AFFECTED
            ? AUDIT_ACTION.AFFECTED_OBJECT_REJECTED
            : AUDIT_ACTION.AFFECTED_OBJECT_ACCEPTED;
        return {
          ...change,
          assessment: { ...assessment, affectedObjects },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              action,
              `Affected object "${label}" set to ${decision}${rationale ? `: ${rationale}` : ''}.`
            ),
          ],
        };
      });

    const submitAssessment = (id: string): TAssessmentSubmission | undefined => {
      const change = changes.find((candidate) => candidate.id === id);
      if (!change?.assessment) return undefined;
      const now = new Date().toISOString();
      const version = (change.assessment.submission?.version ?? 0) + 1;
      const submission = buildSubmissionPreview(change.assessment, change, currentUser, now, version);
      const roleSummary = submission.approverRoles.map((approver) => approver.role).join(', ');
      updateChange(id, (current) => {
        if (!current.assessment) return current;
        return {
          ...current,
          status: CHANGE_STATUS.IN_ASSESSMENT,
          lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
          nextAction: 'Approve assessment',
          progress: Math.max(current.progress, 60),
          assessment: { ...current.assessment, submission },
          auditEvents: [
            ...current.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.ASSESSMENT_SUBMITTED,
              `Assessment submitted (version ${version}).`
            ),
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.APPROVAL_ROUTE_CALCULATED,
              `Approval route calculated: ${roleSummary}.`
            ),
          ],
        };
      });
      return submission;
    };

    const addTemplateCustomField = (input: TAddCustomFieldInput) => {
      const newField: TTemplateCustomFieldConfig = {
        id: `cf-${Math.random().toString(36).slice(2, 8)}`,
        label: input.label,
        fieldType: input.fieldType,
        sectionId: input.sectionId,
        required: input.required,
        prefillKind: input.prefillKind,
        prefilledValue: input.prefilledValue,
        defaultValue: input.defaultValue,
        visibleWhen: input.visibleWhen,
      };
      setAssessmentTemplates((prev) =>
        prev.map((template) => {
          if (template.id !== selectedTemplateId) return template;
          return {
            ...template,
            customFields: [...template.customFields, newField],
            assessmentSections: template.assessmentSections.map((section) =>
              section.id === input.sectionId
                ? { ...section, customFieldIds: [...section.customFieldIds, newField.id] }
                : section
            ),
          };
        })
      );
    };

    // -- Assessment approval -------------------------------------------------

    const decideApproval = (id: string, input: TApprovalInput) =>
      updateChange(id, (change) => {
        if (!change.assessment) return change;
        const now = new Date().toISOString();

        if (input.decision === APPROVAL_DECISION.RETURN) {
          const reopened = { ...change.assessment.sectionProgress };
          (input.returnSections ?? []).forEach((sectionId) => {
            if (reopened[sectionId]) reopened[sectionId] = { sectionId, status: SECTION_STATUS.IN_PROGRESS };
          });
          return {
            ...change,
            status: CHANGE_STATUS.IN_ASSESSMENT,
            lifecycleStep: LIFECYCLE_STEP.ASSESSMENT,
            nextAction: 'Address the requested revisions',
            assessment: { ...change.assessment, sectionProgress: reopened },
            auditEvents: [
              ...change.auditEvents,
              makeAuditEvent(
                currentUser,
                AUDIT_ACTION.ASSESSMENT_RETURNED,
                `Returned for revision: ${input.returnRationale ?? ''}`
              ),
            ],
          };
        }

        if (input.decision === APPROVAL_DECISION.REJECT) {
          return {
            ...change,
            status: CHANGE_STATUS.CANCELLED,
            nextAction: 'Rejected',
            rejectInfo: { rationale: input.rejectRationale ?? '', rejectedBy: currentUser, rejectedAt: now },
            auditEvents: [
              ...change.auditEvents,
              makeAuditEvent(
                currentUser,
                AUDIT_ACTION.ASSESSMENT_REJECTED,
                `Assessment rejected: ${input.rejectRationale ?? ''}`
              ),
            ],
          };
        }

        // Approve the assessment (with or without conditions).
        const withConditions = input.decision === APPROVAL_DECISION.APPROVE_WITH_CONDITIONS;
        const submissionVersion = change.assessment.submission?.version ?? 1;
        const draftPlanningItems = generateDraftPlanningItems(change.assessment);
        return {
          ...change,
          status: CHANGE_STATUS.INITIAL_PLANNING,
          lifecycleStep: LIFECYCLE_STEP.PLANNING,
          nextAction: 'Begin initial planning',
          progress: Math.max(change.progress, 70),
          assessment: {
            ...change.assessment,
            approval: {
              decision: input.decision,
              signedBy: currentUser,
              role: input.signature.role,
              meaning: input.signature.meaning,
              comment: input.signature.comment,
              signedAt: now,
              conditions: withConditions ? input.conditions : undefined,
            },
            baseline: { signedAt: now, signedBy: currentUser, submissionVersion, meaning: input.signature.meaning },
            draftPlanningItems,
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              withConditions ? AUDIT_ACTION.APPROVAL_WITH_CONDITIONS_SIGNED : AUDIT_ACTION.APPROVAL_SIGNED,
              `${input.decision} signed by ${currentUser} (${input.signature.role}).`
            ),
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.SIGNED_BASELINE_CREATED,
              `Signed assessment baseline created (submission version ${submissionVersion}).`
            ),
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.DRAFT_PLANNING_ITEMS_GENERATED,
              `${draftPlanningItems.length} draft planning items generated.`
            ),
          ],
        };
      });

    // -- Implementation plan and execution (Screens 7 and 8) -----------------

    // Updates a Change that has a plan; a Change with no plan is left untouched.
    const updatePlan = (id: string, updater: (plan: TChangePlan, change: TChangeV2) => TChangeV2) =>
      updateChange(id, (change) => (change.plan ? updater(change.plan, change) : change));

    // Applies a task-level update and appends the given audit events.
    const updateTask = (
      id: string,
      taskId: string,
      updater: (task: TPlanTask) => TPlanTask,
      events: TChangeV2['auditEvents']
    ) =>
      updatePlan(id, (plan, change) => ({
        ...change,
        plan: { ...plan, tasks: plan.tasks.map((task) => (task.id === taskId ? updater(task) : task)) },
        auditEvents: [...change.auditEvents, ...events],
      }));

    const ensurePlan = (id: string) =>
      updateChange(id, (change) => {
        if (change.plan) return change;
        const plan = buildPlanFromAssessment(change);
        return {
          ...change,
          plan,
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.PLAN_CREATED,
              `Implementation plan created from the approved assessment (${plan.tasks.length} draft tasks).`
            ),
          ],
        };
      });

    const updatePlanTask = (id: string, taskId: string, patch: Partial<TPlanTask>) =>
      updatePlan(id, (plan, change) => {
        const existing = plan.tasks.find((task) => task.id === taskId);
        if (!existing) return change;
        const events: TChangeV2['auditEvents'] = [];
        if (patch.owner !== undefined && patch.owner !== existing.owner) {
          events.push(
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.TASK_OWNER_CHANGED,
              `Owner of "${existing.title}" set to ${patch.owner || 'Unassigned'}.`
            )
          );
        }
        if (patch.dueDate !== undefined && patch.dueDate !== existing.dueDate) {
          events.push(
            makeAuditEvent(currentUser, AUDIT_ACTION.TASK_DUE_DATE_CHANGED, `Due date of "${existing.title}" changed.`)
          );
        }
        if (events.length === 0) {
          events.push(makeAuditEvent(currentUser, AUDIT_ACTION.TASK_UPDATED, `Task updated: ${existing.title}.`));
        }
        return {
          ...change,
          plan: { ...plan, tasks: plan.tasks.map((task) => (task.id === taskId ? { ...task, ...patch } : task)) },
          auditEvents: [...change.auditEvents, ...events],
        };
      });

    const addPlanTask = (id: string, input: TAddTaskInput) =>
      updatePlan(id, (plan, change) => {
        const task: TPlanTask = {
          id: `task-${makeAuditEvent(currentUser, AUDIT_ACTION.TASK_ADDED, '').id}`,
          title: input.title,
          description: input.description,
          workPackageId: input.workPackageId,
          taskType: input.taskType,
          owner: input.owner,
          responsibleFunction: input.responsibleFunction,
          contributors: [],
          status: TASK_STATUS.NOT_STARTED,
          priority: TASK_PRIORITY.NORMAL,
          dueDate: input.dueDate,
          dependencies: [],
          required: input.required,
          origin: 'Manual',
        };
        return {
          ...change,
          plan: { ...plan, tasks: [...plan.tasks, task] },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.TASK_ADDED, `Task added: ${task.title}.`),
          ],
        };
      });

    const removePlanTask = (id: string, taskId: string) =>
      updatePlan(id, (plan, change) => {
        const existing = plan.tasks.find((task) => task.id === taskId);
        if (!existing) return change;
        return {
          ...change,
          plan: { ...plan, tasks: plan.tasks.filter((task) => task.id !== taskId) },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.TASK_REMOVED, `Task removed: ${existing.title}.`),
          ],
        };
      });

    const startExecution = (id: string) =>
      updatePlan(id, (plan, change) => {
        const active = activeTasks(plan);
        const now = new Date().toISOString();
        const version = (plan.baseline?.version ?? 0) + 1;
        return {
          ...change,
          status: CHANGE_STATUS.IN_EXECUTION,
          lifecycleStep: LIFECYCLE_STEP.EXECUTION,
          nextAction: 'Coordinate execution',
          progress: Math.max(change.progress, 78),
          plan: {
            ...plan,
            baseline: {
              version,
              frozenAt: now,
              frozenBy: currentUser,
              taskCount: active.length,
              workPackageCount: plan.workPackages.length,
              targetDate: plan.targetDate,
            },
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.PLAN_EXECUTION_STARTED,
              `Plan version ${version} frozen and execution started (${active.length} tasks).`
            ),
          ],
        };
      });

    const startTask = (id: string, taskId: string) =>
      updateTask(
        id,
        taskId,
        (task) => ({ ...task, status: TASK_STATUS.IN_PROGRESS }),
        (() => {
          const existing = getChange(id)?.plan?.tasks.find((task) => task.id === taskId);
          return [
            makeAuditEvent(currentUser, AUDIT_ACTION.TASK_STARTED, `Task started: ${existing?.title ?? taskId}.`),
          ];
        })()
      );

    const completeTask = (id: string, taskId: string, input: TCompleteTaskInput) =>
      updatePlan(id, (plan, change) => {
        const existing = plan.tasks.find((task) => task.id === taskId);
        if (!existing) return change;
        const now = new Date().toISOString();
        return {
          ...change,
          plan: {
            ...plan,
            tasks: plan.tasks.map((task) =>
              task.id === taskId
                ? {
                    ...task,
                    status: TASK_STATUS.COMPLETE,
                    completedBy: currentUser,
                    completedAt: now,
                    deviations: input.deviations?.trim() || task.deviations,
                  }
                : task
            ),
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.TASK_COMPLETED, `Task completed: ${existing.title}.`),
          ],
        };
      });

    const reportNewImpact = (id: string, input: TReportImpactInput) =>
      updatePlan(id, (plan, change) => {
        const now = new Date().toISOString();
        const material = input.materialScopeChange === 'Yes';
        const impact: TReportedImpact = {
          id: makeAuditEvent(currentUser, AUDIT_ACTION.NEW_IMPACT_REPORTED, '').id,
          description: input.description.trim(),
          affectedObject: input.affectedObject.trim(),
          materialScopeChange: input.materialScopeChange,
          suggestedFollowUp: input.suggestedFollowUp?.trim() || undefined,
          reportedBy: currentUser,
          reportedAt: now,
          material,
        };
        const events = [
          makeAuditEvent(
            currentUser,
            AUDIT_ACTION.NEW_IMPACT_REPORTED,
            `New impact reported on ${impact.affectedObject}: ${impact.description}`
          ),
        ];
        const blockers = material
          ? [...change.blockers, `Reassessment required: ${impact.description}`]
          : change.blockers;
        if (material) {
          events.push(
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.REASSESSMENT_REQUESTED,
              'Material impact reported. The change requires reassessment before release readiness.'
            )
          );
        }
        return {
          ...change,
          blockers,
          plan: {
            ...plan,
            reportedImpacts: [...plan.reportedImpacts, impact],
            reassessmentRequired: plan.reassessmentRequired || material,
          },
          auditEvents: [...change.auditEvents, ...events],
        };
      });

    const beginCompletion = (id: string) =>
      updateChange(id, (change) => {
        if (change.completion || change.status === CHANGE_STATUS.CLOSED) return change;
        if (!getReleaseReadiness(change).eligible) return change;
        const configuredAnswer = change.assessment?.answers.capaEffectivenessReviewRequired?.value;
        const effectivenessReviewRequired = configuredAnswer === true || configuredAnswer === 'Yes';

        return {
          ...change,
          status: CHANGE_STATUS.IN_COMPLETION,
          lifecycleStep: LIFECYCLE_STEP.COMPLETION,
          progress: Math.max(change.progress, 86),
          nextAction: 'Check release readiness',
          completion: change.completion ?? { effectivenessReviewRequired },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.COMPLETION_STARTED, 'Completion started.'),
          ],
        };
      });

    const checkReleaseReadiness = (id: string) =>
      updateChange(id, (change) => {
        if (!change.completion || !getReleaseReadiness(change).eligible) return change;
        const now = new Date().toISOString();
        return {
          ...change,
          progress: Math.max(change.progress, 89),
          nextAction: 'Approve release',
          completion: {
            ...change.completion,
            releaseReadinessCheckedAt: now,
            releaseReadinessCheckedBy: currentUser,
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.RELEASE_READINESS_CHECKED, 'Release readiness checked.'),
          ],
        };
      });

    const approveRelease = (id: string) =>
      updateChange(id, (change) => {
        if (!change.completion?.releaseReadinessCheckedAt) return change;
        const now = new Date().toISOString();
        const nextAction = change.completion.effectivenessReviewRequired
          ? 'Complete the effectiveness review'
          : 'Close the Change';
        return {
          ...change,
          progress: Math.max(change.progress, 93),
          nextAction,
          completion: {
            ...change.completion,
            releaseApprovedAt: now,
            releaseApprovedBy: currentUser,
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.RELEASE_APPROVED, 'Release approved.'),
          ],
        };
      });

    const completeEffectivenessReview = (id: string) =>
      updateChange(id, (change) => {
        if (!change.completion?.effectivenessReviewRequired || !change.completion.releaseApprovedAt) return change;
        const now = new Date().toISOString();
        return {
          ...change,
          progress: Math.max(change.progress, 97),
          nextAction: 'Close the Change',
          completion: {
            ...change.completion,
            effectivenessReviewedAt: now,
            effectivenessReviewedBy: currentUser,
          },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(
              currentUser,
              AUDIT_ACTION.EFFECTIVENESS_REVIEW_COMPLETED,
              'Required effectiveness review completed.'
            ),
          ],
        };
      });

    const closeChange = (id: string) =>
      updateChange(id, (change) => {
        const completion = change.completion;
        if (
          !completion?.releaseApprovedAt ||
          (completion.effectivenessReviewRequired && !completion.effectivenessReviewedAt)
        )
          return change;
        const now = new Date().toISOString();
        return {
          ...change,
          status: CHANGE_STATUS.CLOSED,
          lifecycleStep: LIFECYCLE_STEP.COMPLETION,
          progress: 100,
          nextAction: 'None',
          currentOwner: undefined,
          completion: { ...completion, closedAt: now, closedBy: currentUser },
          auditEvents: [
            ...change.auditEvents,
            makeAuditEvent(currentUser, AUDIT_ACTION.CHANGE_CLOSED, 'Change closed.'),
          ],
        };
      });

    const addComment = (id: string, body: string) =>
      updateChange(id, (change) => ({
        ...change,
        comments: [
          ...change.comments,
          {
            id: makeAuditEvent(currentUser, AUDIT_ACTION.FIELD_CHANGED, '').id,
            author: currentUser,
            authorRole: role,
            createdAt: new Date().toISOString(),
            body,
          },
        ],
      }));

    const applyPrototypeState = (id: string, state: TPrototypeState) => {
      setSignedBaseline(state === PROTOTYPE_STATE.READ_ONLY);
      setSaveFailureArmed(state === PROTOTYPE_STATE.SAVE_FAILED);
      setPermissionDenied(state === PROTOTYPE_STATE.PERMISSION_DENIED);
      setAutosaveStatus(state === PROTOTYPE_STATE.SAVE_FAILED ? AUTOSAVE_STATUS.FAILED : AUTOSAVE_STATUS.SAVED);

      if (state === PROTOTYPE_STATE.DRAFT) {
        updateChange(id, (change) => ({
          ...change,
          status: CHANGE_STATUS.DRAFT,
          lifecycleStep: LIFECYCLE_STEP.REQUEST,
          returnInfo: undefined,
        }));
      } else if (state === PROTOTYPE_STATE.SUBMITTED) {
        updateChange(id, (change) => ({
          ...change,
          status: CHANGE_STATUS.SUBMITTED,
          lifecycleStep: LIFECYCLE_STEP.REQUEST,
          returnInfo: undefined,
        }));
      } else if (state === PROTOTYPE_STATE.RETURNED) {
        updateChange(id, (change) => ({
          ...change,
          status: CHANGE_STATUS.DRAFT,
          lifecycleStep: LIFECYCLE_STEP.REQUEST,
          returnInfo: change.returnInfo ?? {
            rationale: 'Please clarify the affected products and the desired outcome.',
            returnedBy: currentUser,
            returnedAt: new Date().toISOString(),
          },
        }));
      } else if (state === PROTOTYPE_STATE.CANCELLED) {
        updateChange(id, (change) => ({ ...change, status: CHANGE_STATUS.CANCELLED, nextAction: 'Cancelled' }));
      }
    };

    const retrySave = (id: string) => {
      setSaveFailureArmed(false);
      setAutosaveStatus(AUTOSAVE_STATUS.SAVED);
      updateChange(id, (change) => ({
        ...change,
        auditEvents: [
          ...change.auditEvents,
          makeAuditEvent(currentUser, AUDIT_ACTION.SAVE_FAILED_RETRY_SUCCEEDED, 'Save failed and retry succeeded.'),
        ],
      }));
    };

    return {
      changes,
      templates,
      assessmentTemplates,
      selectedTemplateId,
      previousChanges: MOCK_PREVIOUS_CHANGES,
      currentUser,
      role,
      setRole,
      signedBaseline,
      setSignedBaseline,
      saveFailureArmed,
      permissionDenied,
      autosaveStatus,
      setAutosaveStatus,
      getChange,
      getAssessmentTemplate,
      createDraft,
      patchRequest,
      recordAutosave,
      submitRequest,
      cancelDraft,
      applyTemplate,
      triageAccept,
      triageReturn,
      triageReject,
      triageDuplicate,
      ensureAssessment,
      setAssessmentAnswer,
      setAnswerComment,
      setAnswerRationale,
      setCustomFieldValue,
      confirmAnswerSuggestion,
      completeSection,
      reopenSection,
      setAffectedObjectDecision,
      submitAssessment,
      addTemplateCustomField,
      decideApproval,
      ensurePlan,
      updatePlanTask,
      addPlanTask,
      removePlanTask,
      startExecution,
      startTask,
      completeTask,
      reportNewImpact,
      beginCompletion,
      checkReleaseReadiness,
      approveRelease,
      completeEffectivenessReview,
      closeChange,
      addComment,
      applyPrototypeState,
      retrySave,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    changes,
    templates,
    assessmentTemplates,
    selectedTemplateId,
    role,
    currentUser,
    signedBaseline,
    saveFailureArmed,
    permissionDenied,
    autosaveStatus,
  ]);

  return <ChangeV2Context.Provider value={value}>{children}</ChangeV2Context.Provider>;
};

export const useChangeV2Store = (): TChangeV2StoreValue => useContext(ChangeV2Context);
